import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import SignatureCanvas from 'react-signature-canvas';
import {
  ArrowLeft, ArrowRight, Shield, Pen, X, CheckCircle, FileText, Package, User, Loader2, Camera, Upload
} from 'lucide-react';

const STEPS = [
  { id: 'notes', label: 'Note', icon: FileText },
  { id: 'photos', label: 'Fotografii', icon: Camera },
  { id: 'tech_sig', label: 'Semnatura Ta', icon: Pen },
  { id: 'op_sig', label: 'Semnatura Operator', icon: User },
  { id: 'confirm', label: 'Confirmare', icon: CheckCircle },
];

export default function MobileCloseWizard({ ticket, onClose, onCancel }) {
  const [step, setStep] = useState(0);
  const [notes, setNotes] = useState('');
  const [closureImages, setClosureImages] = useState([]);
  const [operatorSkipped, setOperatorSkipped] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const techSigRef = useRef(null);
  const opSigRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [techSigData, setTechSigData] = useState(null);
  const [opSigData, setOpSigData] = useState(null);

  const canNext = () => {
    if (step === 0) return true; // notes optional
    if (step === 1) return true; // photos optional
    if (step === 2) return !!techSigData; // tech signature required
    if (step === 3) return operatorSkipped || !!opSigData; // op sig or skipped
    return true;
  };

  const handleNext = () => {
    if (step === 2 && techSigRef.current && !techSigRef.current.isEmpty()) {
      setTechSigData(techSigRef.current.toDataURL('image/png'));
    }
    if (step === 3 && opSigRef.current && !opSigRef.current.isEmpty() && !operatorSkipped) {
      setOpSigData(opSigRef.current.toDataURL('image/png'));
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onClose({
        notes,
        technician_signature: techSigData,
        operator_signature: operatorSkipped ? null : opSigData,
        operator_signature_skipped: operatorSkipped,
        operator_skip_reason: operatorSkipped ? skipReason : null,
        closure_images: closureImages.map(img => img.base64),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (closureImages.length + files.length > 5) return;
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) continue;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setClosureImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          preview: ev.target.result,
          base64: ev.target.result.split(',')[1]
        }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const currentStep = STEPS[step];

  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="mobile-close-wizard">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-slate-50">
        <button onClick={onCancel} className="p-1">
          <X className="w-5 h-5 text-slate-500" />
        </button>
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-800">Inchidere Tichet</p>
          <p className="text-[11px] text-slate-500 truncate">{ticket.title}</p>
        </div>
        <Shield className="w-5 h-5 text-slate-400" />
      </div>

      {/* Progress Steps */}
      <div className="flex items-center px-4 py-3 gap-1 border-b">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === step;
          const isDone = i < step;
          return (
            <div key={s.id} className="flex-1 flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                isDone ? 'bg-emerald-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'
              }`}>
                {isDone ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-[9px] font-medium ${isActive ? 'text-blue-600' : 'text-slate-400'}`}>{s.label}</span>
            </div>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        {/* Step 0: Notes */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <FileText className="w-10 h-10 text-blue-500 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-slate-800">Note de Inchidere</h2>
              <p className="text-sm text-slate-500">Descrieti pe scurt ce s-a facut</p>
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: S-a inlocuit rulmentul si s-a reglat axul Y..."
              rows={5}
              className="text-base"
              autoFocus
              data-testid="wizard-notes"
            />
          </div>
        )}

        {/* Step 1: Closure Photos */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <Camera className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-slate-800">Dovada Fotografica</h2>
              <p className="text-sm text-slate-500">Fotografiati lucrarea efectuata (optional, max 5)</p>
            </div>
            {closureImages.length > 0 && (
              <div className="flex gap-2 flex-wrap justify-center">
                {closureImages.map(img => (
                  <div key={img.id} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-green-200">
                    <img src={img.preview} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setClosureImages(prev => prev.filter(i => i.id !== img.id))}
                      className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center">
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {closureImages.length < 5 && (
              <div className="flex gap-3">
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImageSelect} />
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} />
                <Button type="button" variant="outline" className="flex-1 h-14 gap-2 text-base border-green-300 text-green-700 hover:bg-green-50"
                  onClick={() => cameraInputRef.current?.click()} data-testid="wizard-camera-btn">
                  <Camera className="w-5 h-5" /> Fotografiaza
                </Button>
                <Button type="button" variant="outline" className="flex-1 h-14 gap-2 text-base"
                  onClick={() => fileInputRef.current?.click()} data-testid="wizard-upload-btn">
                  <Upload className="w-5 h-5" /> Incarca
                </Button>
              </div>
            )}
            <p className="text-xs text-center text-slate-400">{closureImages.length}/5 fotografii</p>
          </div>
        )}

        {/* Step 2: Technician Signature */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <Pen className="w-10 h-10 text-blue-500 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-slate-800">Semnatura Tehnician</h2>
              <p className="text-sm text-slate-500">Semnati cu degetul in casuta de mai jos</p>
            </div>
            <div className="border-2 border-blue-300 rounded-xl bg-white relative" style={{ height: '200px' }}>
              <SignatureCanvas
                ref={techSigRef}
                penColor="black"
                minWidth={2}
                maxWidth={4}
                canvasProps={{
                  width: 350,
                  height: 196,
                  className: 'rounded-xl',
                  style: { width: '100%', height: '100%', borderRadius: '0.75rem', touchAction: 'none' }
                }}
                backgroundColor="rgb(255,255,255)"
                onEnd={() => setTechSigData(techSigRef.current?.toDataURL('image/png'))}
              />
              <div className="absolute bottom-4 left-0 right-0 border-t border-dashed border-slate-300 mx-8" />
              <span className="absolute bottom-1 right-3 text-[10px] text-slate-400">Semnati aici</span>
            </div>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => { techSigRef.current?.clear(); setTechSigData(null); }}>
              <X className="w-3 h-3 mr-1" /> Sterge semnatura
            </Button>
          </div>
        )}

        {/* Step 3: Operator Signature */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <User className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-slate-800">Semnatura Operator</h2>
              <p className="text-sm text-slate-500">Dati telefonul operatorului pentru semnare</p>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <Checkbox
                checked={operatorSkipped}
                onCheckedChange={(checked) => setOperatorSkipped(checked)}
                id="skip-op-mobile"
                data-testid="wizard-skip-operator"
              />
              <label htmlFor="skip-op-mobile" className="text-sm cursor-pointer text-amber-800">
                Operatorul nu este prezent sau refuza
              </label>
            </div>

            {operatorSkipped ? (
              <Select value={skipReason} onValueChange={setSkipReason}>
                <SelectTrigger className="h-12" data-testid="wizard-skip-reason">
                  <SelectValue placeholder="Selecteaza motivul..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_present">Operator absent</SelectItem>
                  <SelectItem value="refused">Operator refuza semnarea</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <>
                <div className="border-2 border-amber-300 rounded-xl bg-white relative" style={{ height: '200px' }}>
                  <SignatureCanvas
                    ref={opSigRef}
                    penColor="black"
                    minWidth={2}
                    maxWidth={4}
                    canvasProps={{
                      width: 350,
                      height: 196,
                      className: 'rounded-xl',
                      style: { width: '100%', height: '100%', borderRadius: '0.75rem', touchAction: 'none' }
                    }}
                    backgroundColor="rgb(255,255,255)"
                    onEnd={() => setOpSigData(opSigRef.current?.toDataURL('image/png'))}
                  />
                  <div className="absolute bottom-4 left-0 right-0 border-t border-dashed border-slate-300 mx-8" />
                  <span className="absolute bottom-1 right-3 text-[10px] text-slate-400">Semnatura operator</span>
                </div>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => { opSigRef.current?.clear(); setOpSigData(null); }}>
                  <X className="w-3 h-3 mr-1" /> Sterge semnatura
                </Button>
              </>
            )}
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="space-y-5">
            <div className="text-center mb-4">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-slate-800">Confirmare Inchidere</h2>
              <p className="text-sm text-slate-500">Verificati datele inainte de finalizare</p>
            </div>

            <div className="space-y-3">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Note</p>
                <p className="text-sm text-slate-800">{notes || <span className="italic text-slate-400">Fara note</span>}</p>
              </div>

              {closureImages.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Fotografii Dovada ({closureImages.length})</p>
                  <div className="flex gap-2 flex-wrap">
                    {closureImages.map(img => (
                      <img key={img.id} src={img.preview} alt="" className="w-14 h-14 object-cover rounded-lg border" />
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Semnatura Tehnician</p>
                {techSigData ? (
                  <img src={techSigData} alt="Semnatura" className="h-16 border rounded-lg bg-white mx-auto" />
                ) : (
                  <p className="text-sm text-red-500 italic">Lipseste!</p>
                )}
              </div>

              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Semnatura Operator</p>
                {operatorSkipped ? (
                  <p className="text-sm text-amber-600">Omis - {skipReason === 'not_present' ? 'Operator absent' : 'Operator refuza'}</p>
                ) : opSigData ? (
                  <img src={opSigData} alt="Semnatura" className="h-16 border rounded-lg bg-white mx-auto" />
                ) : (
                  <p className="text-sm text-red-500 italic">Lipseste!</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <div className="border-t bg-white px-4 py-3 flex gap-3" style={{paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)'}}>
        {step > 0 ? (
          <Button variant="outline" className="flex-1 h-12" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Inapoi
          </Button>
        ) : (
          <Button variant="outline" className="flex-1 h-12" onClick={onCancel}>
            Anuleaza
          </Button>
        )}

        {step < STEPS.length - 1 ? (
          <Button
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
            onClick={handleNext}
            disabled={!canNext()}
            data-testid="wizard-next-btn"
          >
            Urmatorul <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700"
            onClick={handleSubmit}
            disabled={submitting || !techSigData}
            data-testid="wizard-finish-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
            {submitting ? 'Se proceseaza...' : 'INCHIDE TICHETUL'}
          </Button>
        )}
      </div>
    </div>
  );
}
